import {
  CutlistPart,
  Edgebanding as EdgebandingSides,
  EdgeProfile as EdgeProfileSides,
} from '@cutr/constants/cutlist';
import {
  CutlistOrder,
  CutlistPartType,
  Material,
} from '@cutr/constants/cutlist';
import { getStihoRedirectUrl } from '@cutr/constants/cutlist-theme.ts';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/api/backend';
import { useAgentLoggedIn, useIsLoggedIn } from '@/api/login';
import { getMaterial, getMaterialById } from '@/api/materials';
import { aggregatePrices, lineItemsToMaterials } from '@/api/pricing';
import { useCutlistState } from '@/api/store';
import { AddressReview } from '@/blocks/AddressInfo';
import Card from '@/blocks/Card';
import { EdgesTooltip } from '@/blocks/EdgesTooltip';
import { ErrorMessage } from '@/blocks/ErrorMessage';
import { InfoMessage } from '@/blocks/InfoMessage';
import { AggregatedMaterials, SheetSizeQuantities } from '@/blocks/MaterialRow';
import { TermsAndConditions } from '@/blocks/Price';
import { PricingLineItems } from '@/blocks/PricingLineItems';
import { useFetchMaterials } from '@/hooks';
import { CutlistNotesRecap } from '@/interfaces/CutlistNotesRecap';
import { Button } from '@/primitives/Button';
import {
  Checkmark,
  Dashboard,
  GrainHorizontal,
  GrainNone,
  Icon,
  Layer,
  Menu,
} from '@/primitives/Icons';
import { useAcceptQuote, useGetCutlist } from '@/queries/crud';
import { useCurrentFeatures, useTicker } from '@/theme';
import { currencyFormatter } from '@/utils/format';
import { convertUMToMM } from '@/utils/input';
import { addrToPretty } from '@/utils/misc';

import priceStyles from '../blocks/Price.module.css';
import tableStyles from '../interfaces/PartTableCommon/styles.module.css';
import { Forbidden, NotFound } from './Errors';
import styles from './OrderReview.module.css';

type TableHeader = {
  labelKey: string;
  id?: string;
  width?: string;
  colSpan?: number;
};

export const readOnlyTableHeaders: TableHeader[] = [
  { labelKey: 'field.index.label', id: 'index' },
  { labelKey: 'field.partName.label', id: 'label', width: '220px' },
  { labelKey: 'field.quantity.label' },
  { labelKey: 'field.length.label' },
  { labelKey: 'field.width.label' },
  { labelKey: 'field.edgeband.label', id: 'edgebanding', colSpan: 4 },
  { labelKey: 'field.edgeProfile.label', id: 'edgeProfile', colSpan: 4 },
  { labelKey: 'field.grainDirection.label', id: 'grain' },
  { labelKey: 'field.labeling', id: 'createLabel' },
];

const readOnlyTableHeadersBeams: TableHeader[] = [
  { labelKey: 'field.index.label', id: 'index' },
  { labelKey: 'field.partName.label', id: 'label', width: '160px' },
  { labelKey: 'field.quantity.label' },
  { labelKey: 'field.profileThickness.label' },
  { labelKey: 'field.profileWidth.label' },
  { labelKey: 'field.length.label' },
  { labelKey: 'field.roundedEdge.label', id: 'roundedEdge' },
];

export const useReadOnlyHeaders = (isBeamsGroup = false) => {
  const { hasEdgeProfiling, hasCreateLabels } = useCurrentFeatures();

  if (isBeamsGroup) return readOnlyTableHeadersBeams;

  return readOnlyTableHeaders.filter(({ id }) => {
    if (id === 'edgeProfile') return hasEdgeProfiling;
    if (id === 'createLabel') return hasCreateLabels === 'part';
    return true;
  });
};

const CutlistContext = React.createContext<CutlistOrder>({} as CutlistOrder);

const Cutlist = () => {
  useFetchMaterials();
  const isLoggedIn = useIsLoggedIn();
  const isAgentLoggedIn = useAgentLoggedIn();
  const hasMaterials = useCutlistState((state) => state.hasMaterials);
  const { id } = useParams();
  const { data, isPending, error } = useGetCutlist(id as string);

  if (isPending) return null;

  const errorStatus = (error as ApiError)?.response?.status;
  if (errorStatus === 403) {
    return <Forbidden />;
  }
  if (errorStatus === 404) {
    return <NotFound />;
  }

  return (isLoggedIn || isAgentLoggedIn) && hasMaterials ? (
    <CutlistContext.Provider value={data as CutlistOrder}>
      <CutlistReadOnly />
    </CutlistContext.Provider>
  ) : null;
};

const CutlistReadOnly = () => {
  const { t } = useTranslation();

  return (
    <main className="content">
      <section className="layout">
        <div className={styles.review}>
          <h3>{t('review.subtitle')}</h3>
          <CutlistParts />
          <AddressRecap />
        </div>
        <AsideReadOnly />
      </section>
    </main>
  );
};

const CutlistParts = () => {
  const cutlist = useContext(CutlistContext);

  return (
    <section className={styles.order}>
      <Card>
        <div className="opposites">
          <h3 style={{ textTransform: 'capitalize' }}>{cutlist.title}</h3>
          <span data-testid="cutlist-short-id">{cutlist.shortId}</span>
        </div>

        <div className={styles.summary}>
          <GroupList />

          <CutlistNotesRecap
            customerReference={cutlist.customerReference}
            notes={cutlist.notes}
            deliverLeftoverMaterials={cutlist.deliverLeftoverMaterials}
          />
        </div>
      </Card>
    </section>
  );
};

const GroupList = () => {
  const groupedParts = useGroupedParts();
  const { status } = useContext(CutlistContext);
  const { hasEdgeProfiling, supportedSheetEdgeTrims } = useCurrentFeatures();
  const { t } = useTranslation();

  return (
    <>
      {groupedParts.map((group) => {
        const {
          edgeProfile,
          sheetEdgeTrimType,
          length1TrimThicknessMM,
          materials,
          parts,
          title,
          id,
          continuousGrain,
          additionalProcessing,
          name,
          sheetSizeSelection,
          automaticSheetSizeMaterialIds,
          materialLineItems,
          missingMaterialCodes,
        } = group;
        const isBeamsGroup = parts.every((p) => p.partType === 'beam');

        const automaticSheetSizeMaterials = automaticSheetSizeMaterialIds
          .map(getMaterialById)
          .filter(Boolean) as Material[];
        return (
          <article key={id}>
            <h4>{title}</h4>
            {status === 'quote_ready' && missingMaterialCodes.length > 0 && (
              <InfoMessage
                variant="warning"
                message={t('cutlist-summary.missingMaterialWarning', {
                  codes: missingMaterialCodes.join(', '),
                })}
              />
            )}
            {isBeamsGroup ? (
              <h4 style={{ marginBottom: '1rem' }}>{name}</h4>
            ) : (
              <>
                {sheetSizeSelection === 'automatic' ? (
                  <SheetSizeQuantities
                    automaticSheetSizeMaterials={automaticSheetSizeMaterials}
                    pricingLineItems={materialLineItems}
                  />
                ) : (
                  <AggregatedMaterials items={materials} />
                )}
                {continuousGrain && (
                  <>
                    <p>
                      <strong>
                        {t('cutlist-form.field.continuousGrain.title')}
                      </strong>
                    </p>
                    <p>{continuousGrain}</p>
                  </>
                )}
                {additionalProcessing && (
                  <>
                    <p>
                      <strong>
                        {t('cutlist-form.field.additionalProcessing.title')}
                      </strong>
                    </p>
                    <p>{additionalProcessing}</p>
                  </>
                )}
                {hasEdgeProfiling && edgeProfile !== 'none' && (
                  <p>
                    {t('cutlist-form.edgeProfile')}:{' '}
                    <strong>
                      {t(`cutlist-form.edgeProfiling.${edgeProfile}`)}
                    </strong>
                  </p>
                )}
                {supportedSheetEdgeTrims.length > 1 && sheetEdgeTrimType && (
                  <p>
                    {t('cutlist-form.sheetEdgeTrimTitle')}:{' '}
                    <strong>
                      {t(
                        `cutlist-form.sheetEdgeTrimType.${sheetEdgeTrimType}`,
                        {
                          amount: length1TrimThicknessMM,
                        }
                      )}
                    </strong>
                  </p>
                )}
              </>
            )}

            <div className={tableStyles.orderSection}>
              <PartTable parts={parts}></PartTable>
            </div>
          </article>
        );
      })}
    </>
  );
};

const ReadOnlyPriceDataCard = () => {
  const { t } = useTranslation();
  const cutlist = useContext(CutlistContext);
  const title = cutlist.manualQuote
    ? t('cutlist-summary.price.estimated.title')
    : t('cutlist-summary.price.total.title');
  const priceData = aggregatePrices(cutlist.pricingLineItems);

  const { currency, vatRate } = cutlist;
  const { vatDisplayMode } = useCurrentFeatures();

  const totalAmountExclVAT = cutlist.totalAmountExclVAT;
  const totalAmountInclVAT = cutlist.totalAmountInclVAT;

  return (
    <>
      <div className="opposites">
        <h3>{title}</h3>
        <h3 className="isPrimary" data-testid="total-order-price">
          {currencyFormatter(totalAmountExclVAT / 100, currency)}
        </h3>
      </div>
      <div className={priceStyles.divider}>
        {t('cutlist-summary.price.total.subtitle')}
      </div>

      <PricingLineItems
        priceData={priceData}
        currency={currency}
        vatRate={vatRate}
        vatDisplayMode={vatDisplayMode}
      />

      <ul className={priceStyles.cardPrice}>
        <li className={priceStyles.bolder}>
          <strong>
            {cutlist.manualQuote
              ? t('cutlist-summary.price.estimated.excludingVAT')
              : t('cutlist-summary.price.total.excludingVAT')}
          </strong>
          <strong>
            {currencyFormatter(totalAmountExclVAT / 100, currency)}
          </strong>
        </li>
        <li style={{ height: '0.5rem' }} />
        <li className="flexAlign opposites">
          <span>
            {t('cutlist-summary.price.vatPercent', {
              vatRate,
            })}
          </span>
          <span>
            {currencyFormatter(
              (totalAmountInclVAT - totalAmountExclVAT) / 100,
              currency
            )}
          </span>
        </li>

        <li className={priceStyles.bolder}>
          <strong>
            {cutlist.manualQuote
              ? t('cutlist-summary.price.estimated.includingVAT')
              : t('cutlist-summary.price.total.includingVAT')}
          </strong>
          <strong>
            {currencyFormatter(totalAmountInclVAT / 100, currency)}
          </strong>
        </li>
      </ul>
    </>
  );
};

const AddressRecap = () => {
  const cutlist = useContext(CutlistContext);
  const { i18n } = useTranslation();

  return (
    <>
      {cutlist.addresses.map((address, i) => (
        <AddressReview
          readOnly={true}
          info={addrToPretty(
            { contactName: address.name, ...address },
            i18n.resolvedLanguage
          )}
          key={i}
        />
      ))}
    </>
  );
};

const AsideReadOnly = () => {
  const cutlist = useContext(CutlistContext);

  const { mutateAsync } = useAcceptQuote(cutlist.id);
  const navigate = useNavigate();
  const [error, setError] = React.useState('');
  const source = useTicker();
  const stihoRedirect = getStihoRedirectUrl(
    cutlist.id,
    import.meta.env.VITE_CUTR_ENV
  );

  return (
    <aside>
      {Boolean(error) && <ErrorMessage message={error} />}
      <Price
        onAction={async () => {
          if (source === 'STIHO') {
            window.location.href = stihoRedirect;
            return;
          }

          await mutateAsync()
            .then((acceptedCutlist) => {
              navigate(`/cutlist/${acceptedCutlist.id}/done`, {
                state: { requestType: 'place_order' },
                replace: true,
              });
            })
            .catch((error) => {
              setError(error.message);
            });
        }}
      />
    </aside>
  );
};

const Price = ({ onAction }: { onAction: () => Promise<void> }) => {
  const cutlist = useContext(CutlistContext);

  const [isLoading, setIsLoading] = React.useState(false);
  const { t } = useTranslation();
  const [tosErrorMessage, setTosErrorMessage] = React.useState('');
  const [tosAccepted, setTosAccepted] = React.useState(false);
  const { upfrontPayment } = useCurrentFeatures();

  const onSubmitClick = () => {
    if (!tosAccepted) {
      setTosErrorMessage(t('common.termsAndConditionsErrorMessage'));
      return;
    }
    setIsLoading(true);
    onAction().finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <Card>
      {upfrontPayment && (
        <InfoMessage message={t('cutlist-summary.price.payment')} />
      )}
      <ReadOnlyPriceDataCard />
      {cutlist.status === 'quote_ready' && (
        <div>
          <TermsAndConditions
            tosAccepted={tosAccepted}
            setTosAccepted={setTosAccepted}
            tosErrorMessage={tosErrorMessage}
            setTosErrorMessage={setTosErrorMessage}
          />
          <Button
            disabled={isLoading || !tosAccepted}
            onClick={() => onSubmitClick()}
          >
            {t('cutlist-summary.price.total.acceptQuote')}
          </Button>
        </div>
      )}
    </Card>
  );
};

const PartTable = ({ parts }: { parts: CutlistPart[] }) => {
  const isBeamsGroup = parts.every((p) => p.partType === 'beam');
  return (
    <table>
      <Head isBeamsGroup={isBeamsGroup} />
      <Body parts={parts} />
    </table>
  );
};

const Head = ({ isBeamsGroup = false }: { isBeamsGroup?: boolean }) => {
  const headers = useReadOnlyHeaders(isBeamsGroup);
  const {
    edgebandingUI: hasEdgebanding,
    hasEdgeProfiling,
    hasCreateLabels,
  } = useCurrentFeatures();
  const { t } = useTranslation();

  const headerText = (key?: string) => (key ? t(`cutlist-form.${key}`) : '');

  return (
    <thead>
      <tr>
        {headers.map((h, i) => {
          const edgesHeader = (
            <th
              key={h.labelKey || i}
              style={{ minWidth: h.width }}
              colSpan={h.colSpan}
            >
              <div>
                {t(`cutlist-form.${h.labelKey}`)}
                <EdgesTooltip />
              </div>
            </th>
          );

          switch (h.id) {
            case 'edgebanding':
              if (!hasEdgebanding) return null;
              return edgesHeader;
            case 'edgeProfile':
              if (!hasEdgeProfiling) return null;
              return edgesHeader;
          }

          return (
            <th key={h.labelKey || i} style={{ minWidth: h.width }} colSpan={1}>
              {headerText(h.labelKey)}
            </th>
          );
        })}
      </tr>
      {!isBeamsGroup && (hasEdgebanding || hasEdgeProfiling) && (
        <tr>
          <th />
          <th />
          <th />
          <th />
          <th />
          <th>{t('cutlist-form.field.edgebanding.sides.l1')}</th>
          <th>{t('cutlist-form.field.edgebanding.sides.l2')}</th>
          <th>{t('cutlist-form.field.edgebanding.sides.w1')}</th>
          <th>{t('cutlist-form.field.edgebanding.sides.w2')}</th>
          <th />
          {hasCreateLabels === 'part' && <th />}
        </tr>
      )}
    </thead>
  );
};

const Body = ({ parts }: { parts: CutlistPart[] }) => {
  return (
    <tbody>
      {parts.map((part) => {
        return <TableRow part={part} key={part.id} />;
      })}
    </tbody>
  );
};

const TableRow = ({ part }: { part: CutlistPart }) => {
  const {
    edgebandingUI: hasEdgebanding,
    hasEdgeProfiling,
    hasCreateLabels,
  } = useCurrentFeatures();

  const isBeamPart = part.partType === 'beam';

  const iconMap: Record<CutlistPartType, React.JSX.Element> = {
    panel: <Dashboard />,
    sheet: <Layer />,
    strip: <Menu />,
    beam: <Dashboard />,
  };

  const edgebandingSides: (keyof EdgebandingSides)[] = [
    'length1',
    'length2',
    'width1',
    'width2',
  ];

  const edgeProfilingSides: (keyof EdgeProfileSides)[] = [
    'length1',
    'length2',
    'width1',
    'width2',
  ];

  if (isBeamPart) {
    return <TableRowBeam part={part} />;
  }

  return (
    <tr id={`panelRow-${part.id}`}>
      <td> {part.index + 1} </td>
      <td>
        {
          <span className="flexAlign gap-xs">
            <Icon
              className={styles.unflex}
              icon={iconMap[part.partType] || <Dashboard />}
              color="var(--gray-7)"
            />
            {part.label}
          </span>
        }
      </td>
      <td>
        <span>{part.quantity}</span>
      </td>
      <td>
        <span>{part.lengthMM}</span>
      </td>
      <td>
        <span>{part.widthMM}</span>
      </td>

      {hasEdgebanding &&
        edgebandingSides.map((side) => (
          <td key={side} style={{ color: 'var(--primary)' }}>
            {Boolean(part.edgebanding?.[side]) && (
              <Icon icon={<Checkmark />}></Icon>
            )}
          </td>
        ))}

      {hasEdgeProfiling &&
        edgeProfilingSides.map((side) => (
          <td key={side} style={{ color: 'var(--primary)' }}>
            {Boolean(
              part.edgeProfile?.[side] && part.edgeProfile?.[side] !== 'none'
            ) && <Icon icon={<Checkmark />}></Icon>}
          </td>
        ))}

      <td style={{ color: 'var(--primary)' }}>
        {part.grainDirection === 'along' ? (
          <Icon icon={<GrainHorizontal />} />
        ) : (
          <Icon icon={<GrainNone />} />
        )}
      </td>

      {hasCreateLabels === 'part' && (
        <td style={{ color: 'var(--primary)' }}>
          {Boolean(part.createLabel) && <Icon icon={<Checkmark />}></Icon>}
        </td>
      )}
    </tr>
  );
};

const TableRowBeam = ({ part }: { part: CutlistPart }) => {
  return (
    <tr id={`panelRow-${part.id}`}>
      <td> {part.index + 1} </td>

      <td>
        {
          <span className="flexAlign gap-xs">
            <Icon
              className={styles.unflex}
              icon={<Dashboard />}
              color="var(--gray-7)"
            />
            {part.label}
          </span>
        }
      </td>

      <td>
        <span>{part.quantity}</span>
      </td>

      <td>
        <span>{convertUMToMM(part.thicknessUM)}</span>
      </td>

      <td>
        <span>{part.widthMM}</span>
      </td>

      <td>
        <span>{part.lengthMM}</span>
      </td>

      <td>
        <span>{part.beamRounded ? 'Yes' : 'No'}</span>
      </td>
    </tr>
  );
};

const useGroupedParts = () => {
  const { pricingLineItems, materialGroups } = useContext(CutlistContext);
  const allMaterials = lineItemsToMaterials(pricingLineItems);

  return materialGroups.map((g) => {
    const groupParts = g.parts;

    const edgeband = groupParts
      .flatMap((p) => [
        p.width1Edgeband?.articleCode,
        p.width2Edgeband?.articleCode,
        p.length1Edgeband?.articleCode,
        p.length2Edgeband?.articleCode,
      ])
      .filter(Boolean);

    const expectedArticleCodes = [
      ...new Set(
        [
          ...edgeband,
          g.bottomHpl?.articleCode,
          g.topHpl?.articleCode,
          g.core1Material?.articleCode,
          g.core2Material?.articleCode,
        ].filter(Boolean)
      ),
    ] as string[];

    const currentGroupMaterials = allMaterials
      .filter(Boolean)
      .filter((material) =>
        expectedArticleCodes.includes(material.articleCode)
      );

    const foundArticleCodes = new Set(
      currentGroupMaterials.map((m) => m.articleCode)
    );
    const missingMaterialCodes = expectedArticleCodes.filter(
      (code) => !foundArticleCodes.has(code)
    );

    const material1 = getMaterial(g.core1Material?.articleCode);
    const material2 = getMaterial(g.core2Material?.articleCode);
    const coreName = (m: ReturnType<typeof getMaterial>) =>
      g.sheetSizeSelection === 'automatic'
        ? m?.variationGroup?.name || m?.name
        : m?.name;
    const name = [coreName(material1), coreName(material2)]
      .filter(Boolean)
      .join(' / ');
    const title = [name, g.topHpl?.name, g.bottomHpl?.name]
      .filter(Boolean)
      .join(' / ');

    const materialLineItems = (pricingLineItems || []).filter(
      (item) => item.groupId === g.id
    );

    return {
      ...g,
      title,
      id: g.id,
      materials: currentGroupMaterials,
      missingMaterialCodes,
      materialLineItems,
      core1: g.core1Material,
      parts: groupParts,
      edgeProfile: g.edgeProfileType,
    };
  });
};

export default Cutlist;
